// ============================================================
// TERROR ENGINE — PLAYER ENTITY
// Universal player + companion rendering.
// Used by Plaza, Workshop, FactionHall, Dungeon, and any future zone.
//
// Props:
//   pos        { x, y }         — tile position
//   facing     { x, y }         — direction (-1/0/1)
//   gender     "f" | "m"
//   walkFrame  boolean           — alternates for walk animation
//   isIdle     boolean
//   idleFrame  boolean           — female idle _b frame
//   tile       number            — tile size in px
//   opacity    number            — for dungeon fade effects
//   companionId string | null    — "default" | "jynxi" | null
//   showCompanion boolean
//   size       number            — multiplier (default 1.5 × tile)
//   equipped   { head, face, body, back, aura, title } | null
//                                 — cosmetic overlay; null = no cosmetics rendered
//   playerNode    ReactNode | null — OVERRIDE (July 16, Annihilation
//                 continuity batch): when provided, renders INSIDE the
//                 engine's positioned player div in place of the img/
//                 fallback. Positioning math stays here (one source);
//                 the caller only decides what fills the frame.
//                 Terror rooms pass nothing — behavior unchanged.
//   companionNode ReactNode | null — same override for the companion
//                 slot. When provided, the companion div renders it
//                 instead of the manifest-art <img> chain.
// ============================================================

import { useState, useEffect, useRef, useCallback } from "react";
import { getPlayerSprite, getIdleSprite, getCompanionSprite, getCompanionFallback } from "./SpriteManifest";
import { CosmeticOverlay } from "./CosmeticOverlay";

export function PlayerEntity({
  pos,
  facing     = { x: 0, y: 1 },
  gender     = "f",
  walkFrame  = false,
  isIdle     = false,
  idleFrame  = false,
  tile       = 48,
  opacity    = 1,
  companionId = null,
  showCompanion = true,
  equipped   = null,
  playerNode = null,
  companionNode = null,
}) {
  const size = Math.floor(tile * 1.5);

  // ── Player sprite resolution ──────────────────────────────
  const src = isIdle
    ? getIdleSprite(gender, idleFrame)
    : getPlayerSprite(gender, facing.x, facing.y, walkFrame);

  const [imgErr, setImgErr] = useState(false);
  const prevSrc = useRef(src);
  useEffect(() => {
    if (prevSrc.current !== src) { prevSrc.current = src; setImgErr(false); }
  }, [src]);

  // ── Companion position — trails behind player ─────────────
  // companionSrc is the companion's OWN derived art; if that file
  // doesn't exist yet it 404s and we swap to J'ynxi (the prototype)
  // rather than hiding the companion — a room never goes companion-less.
  const ownCompanionSrc = companionId ? getCompanionSprite(companionId) : null;
  const [compFellBack, setCompFellBack] = useState(false);
  const prevCompSrc = useRef(ownCompanionSrc);
  useEffect(() => {
    if (prevCompSrc.current !== ownCompanionSrc) {
      prevCompSrc.current = ownCompanionSrc;
      setCompFellBack(false); // new companion — try its own art again
    }
  }, [ownCompanionSrc]);
  const companionSrc = compFellBack ? getCompanionFallback() : ownCompanionSrc;
  const companionSize = Math.floor(tile * 1.3);
  const fx = facing.x || 0;
  const fy = facing.y || 1;
  const gap = tile * 1.5;
  const compLeft = pos.x * tile - fx * gap * 2 - companionSize / 2 + tile / 2;
  const compTop  = pos.y * tile + (fy === -1 ? gap * 2 : -fy * gap) - companionSize + tile;

  return (
    <>
      {/* Companion */}
      {showCompanion && (companionNode || companionSrc) && (
        <div style={{
          position: "absolute",
          left: compLeft, top: compTop,
          width: companionSize, height: companionSize,
          zIndex: 9,
          transition: "left 0.12s, top 0.12s",
          pointerEvents: "none",
          filter: "drop-shadow(0 0 6px #c9a84c44)",
        }}>
          {companionNode ? companionNode : (
            <img
              key={companionSrc}
              src={companionSrc}
              alt="companion"
              onError={() => { if (!compFellBack) setCompFellBack(true); }}
              style={{ width: "100%", height: "100%", imageRendering: "pixelated" }}
            />
          )}
        </div>
      )}

      {/* Player */}
      <div style={{
        position: "absolute",
        left: pos.x * tile - size / 2 + tile / 2,
        top:  pos.y * tile - size + tile,
        width: size, height: size,
        zIndex: 10,
        transition: "left 0.08s, top 0.08s, opacity 0.3s ease",
        opacity,
        pointerEvents: "none",
      }}>
        {playerNode
          ? playerNode
          : src && !imgErr
          ? <img
              key={src}
              src={src}
              alt="player"
              onError={() => setImgErr(true)}
              style={{ width: "100%", height: "100%", imageRendering: "pixelated" }}
            />
          : <div style={{
              width: "100%", height: "100%",
              background: "#c9a84c",
              borderRadius: 4,
              border: "2px solid #d4a84b",
            }} />
        }

        {/* Cosmetic overlay — no-op when equipped is null */}
        <CosmeticOverlay equipped={equipped} size={size} gender={gender} facing={facing} />
      </div>
    </>
  );
}

// ── Walk/idle animation hook ──────────────────────────────────
// Drop into any zone's main component to get walk + idle state.
// Returns { walkFrame, isIdle, idleFrame } to pass into PlayerEntity.
export function usePlayerAnimation(pos) {
  const [walkFrame, setWalkFrame]   = useState(false);
  const [isIdle, setIsIdle]         = useState(false);
  const [idleFrame, setIdleFrame]   = useState(false);
  const walkFrameRef  = useRef(false);
  const lastPosRef    = useRef({ x: pos.x, y: pos.y });
  const idleTimerRef  = useRef(null);
  const idleAnimRef   = useRef(null);

  useEffect(() => {
    if (pos.x !== lastPosRef.current.x || pos.y !== lastPosRef.current.y) {
      lastPosRef.current = { x: pos.x, y: pos.y };
      walkFrameRef.current = !walkFrameRef.current;
      setWalkFrame(walkFrameRef.current);
      setIsIdle(false);
      setIdleFrame(false);
      clearTimeout(idleTimerRef.current);
      clearInterval(idleAnimRef.current);
      idleTimerRef.current = setTimeout(() => {
        setIsIdle(true);
        idleAnimRef.current = setInterval(() => setIdleFrame(f => !f), 1800);
      }, 3000);
    }
    return () => {      clearTimeout(idleTimerRef.current);
      clearInterval(idleAnimRef.current);
    };
  }, [pos.x, pos.y]);

  return { walkFrame, isIdle, idleFrame };
}

// ── Mobile D-Pad ─────────────────────────────────────────────
// Identical across all zones. One definition, imported everywhere.
export function MobileDpad({ keysRef }) {
  const dirs = [
    { key: "ArrowUp",    label: "▲", col: 2, row: 1 },
    { key: "ArrowLeft",  label: "◀", col: 1, row: 2 },
    { key: "ArrowDown",  label: "▼", col: 2, row: 3 },
    { key: "ArrowRight", label: "▶", col: 3, row: 2 },
  ];
  const press    = (key) => { keysRef.current[key] = true; };
  const release  = (key) => { keysRef.current[key] = false; };
  const releaseAll = () => {
    ["ArrowUp","ArrowDown","ArrowLeft","ArrowRight"].forEach(k => { keysRef.current[k] = false; });
  };
  const btn = (col, row) => ({
    gridColumn: col, gridRow: row,
    width: 48, height: 48,
    background: "rgba(10,5,30,0.85)",
    border: "1px solid rgba(161,130,250,0.4)",
    borderRadius: 10, color: "rgba(200,180,255,0.9)", fontSize: 18,
    display: "flex", alignItems: "center", justifyContent: "center",
    cursor: "pointer", userSelect: "none", WebkitUserSelect: "none",
    touchAction: "none", boxShadow: "0 2px 10px rgba(0,0,0,0.7)",
  });
  return (
    <div style={{
      position: "fixed", bottom: 24, right: 20, zIndex: 200,
      display: "grid",
      gridTemplateColumns: "48px 48px 48px",
      gridTemplateRows: "48px 48px 48px",
      gap: 5, touchAction: "none",
    }}>
      {dirs.map(({ key, label, col, row }) => (
        <div key={key} style={btn(col, row)}
          onPointerDown={e => { e.preventDefault(); press(key); }}
          onPointerUp={e => { e.preventDefault(); release(key); }}
          onPointerLeave={e => { e.preventDefault(); release(key); }}
          onPointerCancel={e => { e.preventDefault(); releaseAll(); }}>
          {label}
        </div>
      ))}
    </div>
  );
}

// ── Mobile Action Pad ────────────────────────────────────────
// Batch 5 extension (Annihilation) — attack + dodge buttons,
// bottom-LEFT (the D-pad owns bottom-right). Same visual family
// as MobileDpad; identical across all zones, one definition.
// Pass the controller's verbs straight in:
//   <MobileActionPad onAttack={ctrl.attack} onDodge={ctrl.dodge} />
export function MobileActionPad({ onAttack, onDodge, attackLabel = "⚔", dodgeLabel = "✦" }) {
  const btn = (size) => ({
    width: size, height: size,
    background: "rgba(10,5,30,0.85)",
    border: "1px solid rgba(161,130,250,0.4)",
    borderRadius: "50%", color: "rgba(200,180,255,0.9)",
    fontSize: size * 0.42,
    display: "flex", alignItems: "center", justifyContent: "center",
    cursor: "pointer", userSelect: "none", WebkitUserSelect: "none",
    touchAction: "none", boxShadow: "0 2px 10px rgba(0,0,0,0.7)",
  });
  return (
    <div style={{
      position: "fixed", bottom: 28, left: 20, zIndex: 200,
      display: "flex", alignItems: "flex-end", gap: 12, touchAction: "none",
    }}>
      <div style={btn(48)}
        onPointerDown={e => { e.preventDefault(); onDodge && onDodge(); }}>
        {dodgeLabel}
      </div>
      <div style={btn(64)}
        onPointerDown={e => { e.preventDefault(); onAttack && onAttack(); }}>
        {attackLabel}
      </div>
    </div>
  );
}
